/**
 * Webhook Settings Page - Manage organization webhooks
 */

import * as Dialog from '@radix-ui/react-dialog';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { webhooksApi, type Webhook, type WebhookDelivery, type CreateWebhookRequest } from '@/services/api/webhooks';
import { useAuthStore } from '@/stores/authStore';
import { WebhookForm } from '@/features/settings/WebhookForm';
import { useConfirmDialog } from '@/components/ui/ConfirmDialog';
import { toast } from '@components/ui/Toast';

const EVENT_TYPES = [
  { value: 'task.created', label: 'Task Created' },
  { value: 'task.updated', label: 'Task Updated' },
  { value: 'task.deleted', label: 'Task Deleted' },
  { value: 'task.moved', label: 'Task Moved' },
  { value: 'task.assigned', label: 'Task Assigned' },
  { value: 'task.completed', label: 'Task Completed' },
  { value: 'project.created', label: 'Project Created' },
  { value: 'project.updated', label: 'Project Updated' },
  { value: 'comment.created', label: 'Comment Created' },
  { value: 'member.joined', label: 'Member Joined' },
  { value: 'member.removed', label: 'Member Removed' },
];

export const WebhookSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const organizationId = useAuthStore((state) => state.currentOrgId);
  const scope = useRef(organizationId);
  scope.current = organizationId;
  const deliveryRequest = useRef(0);
  const [createdSecret, setCreatedSecret] = useState<string | null>(null);
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState<Webhook | null>(null);
  const [selectedWebhook, setSelectedWebhook] = useState<Webhook | null>(null);
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [loadingDeliveries, setLoadingDeliveries] = useState(false);

  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  useEffect(() => {
    deliveryRequest.current += 1;
    setError(null);
    setEditingWebhook(null);
    setWebhooks([]);
    setSelectedWebhook(null);
    setDeliveries([]);
    setShowForm(false);
    setCreatedSecret(null);
    if (organizationId) void fetchWebhooks();
    else setLoading(false);
  }, [organizationId]);

  const fetchWebhooks = async () => {
    try {
      setLoading(true);
      setError(null);
      if (!organizationId) return;
      const response = await webhooksApi.listWebhooks(organizationId);
      if (scope.current === organizationId) setWebhooks(response.webhooks);
    } catch (err) {
      console.error('Failed to fetch webhooks:', err);
      if (scope.current === organizationId) setError(t('settings.webhooks.loadFailed', 'Failed to load webhooks'));
    } finally {
      if (scope.current === organizationId) setLoading(false);
    }
  };

  const fetchDeliveries = async (webhookId: string) => {
    const request = ++deliveryRequest.current;
    setDeliveries([]);
    try {
      setLoadingDeliveries(true);
      if (!organizationId) return;
      const response = await webhooksApi.listDeliveries(organizationId, webhookId);
      if (scope.current === organizationId && request === deliveryRequest.current) setDeliveries(response.deliveries);
    } catch (err) {
      console.error('Failed to fetch deliveries:', err);
    } finally {
      if (scope.current === organizationId && request === deliveryRequest.current) setLoadingDeliveries(false);
    }
  };

  const handleCreate = () => {
    setEditingWebhook(null);
    setShowForm(true);
  };

  const handleEdit = (webhook: Webhook) => {
    setEditingWebhook(webhook);
    setShowForm(true);
  };

  const handleDelete = async (webhookId: string) => {
    const confirmed = await confirm({
      title: t('settings.webhooks.deleteTitle', 'Delete Webhook'),
      description: t('settings.webhooks.deleteConfirm', 'Are you sure you want to delete this webhook? This action cannot be undone.'),
      confirmLabel: t('common.delete', 'Delete'),
      variant: 'danger',
    });

    if (!confirmed || scope.current !== organizationId) return;

    try {
      if (!organizationId) return;
      await webhooksApi.deleteWebhook(organizationId, webhookId);
      if (scope.current !== organizationId) return;
      setWebhooks((items) => items.filter((w) => w.id !== webhookId));
      if (selectedWebhook?.id === webhookId) {
        setSelectedWebhook(null);
        setDeliveries([]);
      }
    } catch (err) {
      console.error('Failed to delete webhook:', err);
      toast.error(t('settings.webhooks.deleteFailed', 'Failed to delete webhook'));
    }
  };

  const handleToggle = async (webhook: Webhook) => {
    try {
      if (!organizationId) return;
      const response = await webhooksApi.updateWebhook(organizationId, webhook.id, {
        enabled: !webhook.enabled,
      });
      if (scope.current !== organizationId) return;
      setWebhooks((items) => items.map((w) => (w.id === webhook.id ? response : w)));
    } catch (err) {
      console.error('Failed to toggle webhook:', err);
      toast.error(t('settings.webhooks.toggleFailed', 'Failed to update webhook'));
    }
  };

  const handleTest = async (webhookId: string) => {
    try {
      if (!organizationId) return;
      await webhooksApi.testWebhook(organizationId, webhookId);
      toast.success(t('settings.webhooks.testSent', 'Test webhook sent! Check recent deliveries.'));
      if (selectedWebhook?.id === webhookId) {
        fetchDeliveries(webhookId);
      }
    } catch (err) {
      console.error('Failed to send test webhook:', err);
      toast.error(t('settings.webhooks.testFailed', 'Failed to send test webhook'));
    }
  };

  const handleFormSave = async (data: CreateWebhookRequest) => {
    if (!organizationId) return;
    try {
      if (editingWebhook) {
        const response = await webhooksApi.updateWebhook(organizationId, editingWebhook.id, data);
        if (scope.current !== organizationId) return;
        setWebhooks((items) => items.map((w) => (w.id === editingWebhook.id ? response : w)));
      } else {
        const response = await webhooksApi.createWebhook(organizationId, data);
        if (scope.current !== organizationId) return;
        setWebhooks((items) => [...items, response]);
        setCreatedSecret(response.secret);
      }
      setShowForm(false);
      setEditingWebhook(null);
    } catch (err) {
      console.error('Failed to save webhook:', err);
      throw err;
    }
  };

  const handleSelectWebhook = (webhook: Webhook) => {
    setSelectedWebhook(webhook);
    fetchDeliveries(webhook.id);
  };

  const getStatusBadge = (status: WebhookDelivery['status']) => {
    const styles: Record<string, string> = {
      delivered: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      pending: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
      sending: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      retrying: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status]}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
          <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('settings.webhooks.title', 'Webhooks')}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {t('settings.webhooks.subtitle', 'Send real-time event notifications to external services')}
          </p>
        </div>
        <button
          disabled={!organizationId}
          onClick={handleCreate}
          className="px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-800 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t('settings.webhooks.create', 'Create Webhook')}
        </button>
      </div>

      {createdSecret && (
        <div role="status" className="rounded-lg border border-primary-200 bg-primary-50 p-4 dark:border-primary-800 dark:bg-primary-950">
          <p className="text-sm font-medium">{t('settings.webhooks.secretNotice', 'Save this signing secret. It is only shown once.')}</p>
          <code className="mt-2 block break-all select-all text-sm">{createdSecret}</code>
          <button onClick={() => setCreatedSecret(null)} className="mt-3 text-sm underline">{t('common.close', 'Close')}</button>
        </div>
      )}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Webhooks List */}
      {!error && (webhooks.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 text-center">
          <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {t('settings.webhooks.empty', 'No webhooks configured')}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {t('settings.webhooks.emptyDesc', 'Create a webhook to start receiving event notifications')}
          </p>
          <button
            disabled={!organizationId}
          onClick={handleCreate}
            className="px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-800"
          >
            {t('settings.webhooks.createFirst', 'Create your first webhook')}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Webhooks Column */}
          <div className="space-y-4">
            {webhooks.map((webhook) => (
              <div
                key={webhook.id}
                className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border-2 transition-colors cursor-pointer ${
                  selectedWebhook?.id === webhook.id
                    ? 'border-blue-500'
                    : 'border-transparent hover:border-gray-200 dark:hover:border-gray-700'
                }`}
                onClick={() => handleSelectWebhook(webhook)}
              >
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${webhook.enabled ? 'bg-green-500' : 'bg-gray-400'}`} />
                        <h3 className="font-medium text-gray-900 dark:text-white truncate">
                          {webhook.url}
                        </h3>
                      </div>
                      {webhook.description && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          {webhook.description}
                        </p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                        <span>{webhook.events.length || 'All'} events</span>
                        <span>{webhook.deliveryCount} deliveries</span>
                        {webhook.failureCount > 0 && (
                          <span className="text-red-500">{webhook.failureCount} failed</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => handleToggle(webhook)}
                        className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        title={webhook.enabled ? 'Disable' : 'Enable'}
                      >
                        {webhook.enabled ? (
                          <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                      <button
                        onClick={() => handleTest(webhook.id)}
                        className="p-2 text-gray-400 hover:text-blue-600"
                        title="Send test"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleEdit(webhook)}
                        className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        title="Edit"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDelete(webhook.id)}
                        className="p-2 text-gray-400 hover:text-red-600"
                        title="Delete"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Deliveries Column */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="font-medium text-gray-900 dark:text-white">
                {t('settings.webhooks.recentDeliveries', 'Recent Deliveries')}
              </h3>
            </div>
            <div className="p-4">
              {!selectedWebhook ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                  {t('settings.webhooks.selectToView', 'Select a webhook to view deliveries')}
                </p>
              ) : loadingDeliveries ? (
                <div className="animate-pulse space-y-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  ))}
                </div>
              ) : deliveries.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                  {t('settings.webhooks.noDeliveries', 'No deliveries yet')}
                </p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {deliveries.map((delivery) => (
                    <div
                      key={delivery.id}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg"
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          {getStatusBadge(delivery.status)}
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {delivery.eventType}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {new Date(delivery.createdAt).toLocaleString()}
                          {delivery.durationMs && ` • ${delivery.durationMs}ms`}
                          {delivery.responseStatus && ` • HTTP ${delivery.responseStatus}`}
                        </div>
                        {delivery.errorMessage && (
                          <p className="text-xs text-red-500 mt-1">{delivery.errorMessage}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}

      {/* Webhook Form Modal */}
      {showForm && (
        <Dialog.Root open={showForm} onOpenChange={setShowForm}>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-[70] bg-neutral-950/45" />
            <Dialog.Content aria-describedby={undefined} className="workspace-surface fixed left-1/2 top-1/2 z-[71] max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl bg-white shadow-xl dark:bg-neutral-900">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingWebhook
                  ? t('settings.webhooks.edit', 'Edit Webhook')
                  : t('settings.webhooks.create', 'Create Webhook')}
              </Dialog.Title>
            </div>
            <WebhookForm
              webhook={editingWebhook}
              eventTypes={EVENT_TYPES}
              onSave={handleFormSave}
              onCancel={() => {
                setShowForm(false);
                setEditingWebhook(null);
              }}
            />
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}

      {ConfirmDialogComponent}
    </div>
  );
};

export default WebhookSettingsPage;
